import * as React from "react";
import { ValueType } from "../../types";
import { Formik, Field, FormikErrors } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import moment from "moment";
import FormikDob from "../../../components/form/FormikDob";
import {
  getPatientFormParams,
  getPatientFormParamsRequest,
  Patient,
  PatientFormParams
} from "../types";

interface OwnProps {
  locations: string[];
  patient?: Patient;
  action: (
    params: PatientFormParams,
    onError: (error: FormikErrors<PatientFormParams>) => void,
    id?: string
  ) => void;
  actionText: string;
}
interface OwnState {
  selectedLocation?: ValueType | null;
  previousPhone: string;
}

type Props = OwnProps;

class PatientAddEdit extends React.PureComponent<Props, OwnState> {
  constructor(props: Props) {
    super(props);

    const selectedLocationDropdown = this.props.patient?.location
      ? {
          value: this.props.patient?.location,
          label: this.props.patient?.location
        }
      : null;

    this.state = { selectedLocation: selectedLocationDropdown, previousPhone: "" };
  }

  private locationOptions = () => {
    return this.props.locations.map(location => {
      return { value: location, label: location };
    });
  };

  public action = (
    params: PatientFormParams,
    onError: (error: FormikErrors<PatientFormParams>) => void,
    id?: string
  ) => {
    // must transform values from UI friendly to API friendly...
    this.props.action(getPatientFormParamsRequest(params), onError, id);
  };

  private getInitialValues = (patient?: Patient): PatientFormParams => {
    if (patient) {
      return getPatientFormParams(patient);
    } else {
      return {
        accountId: "",
        mrn: "",
        givenName: "",
        familyName: "",
        dateOfBirth: "",
        email: "",
        phone: "",
        location: ""
      };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateLocation = (e: any, setFieldValue: any) => {
    setFieldValue("location", e.value, false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePhone(e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) {
    const value = e.target.value;
    setFieldValue("phone", this.formatPhone(value), true);
  }

  formatPhone = (value: string, forceUpdate?: boolean) => {
    let formattedvalue = "";
    const currentValue = value.replace(/[^\d]/g, "");
    const cvLength = currentValue.length;

    if (
      !this.state.previousPhone ||
      value.length > this.state.previousPhone.replace(/[^\d]/g, "").length ||
      forceUpdate
    ) {
      if (cvLength <= 3) formattedvalue = currentValue;
      if (cvLength >= 4 && cvLength <= 6)
        formattedvalue = `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
      if (cvLength > 6)
        formattedvalue = `(${currentValue.slice(0, 3)}) ${currentValue.slice(
          3,
          6
        )}-${currentValue.slice(6, 10)}`;
    } else {
      formattedvalue = value;
    }
    this.setState({ previousPhone: formattedvalue });
    return formattedvalue;
  };

  render() {
    const { actionText, patient } = this.props;
    return (
      <div className="addPatientContainer">
        {patient ? <h1>Edit Patient</h1> : <h1>Add New Patient</h1>}

        <Formik
          initialValues={this.getInitialValues(patient)}
          onSubmit={async (values, form) => {
            this.action(values, form.setErrors, patient?.id);
          }}
          validationSchema={Yup.object().shape({
            givenName: Yup.string().required("Required"),
            familyName: Yup.string().required("Required"),
            phone: Yup.string().test(
              "len",
              "Please enter valid phone number.",
              val => (val && val.replace(/[^\d]/g, "").length === 10) || !val
            ),
            email: Yup.string()
              .email()
              .nullable(),
            dateOfBirth: Yup.string().test(
              "date",
              "Please enter a valid Date of Birth",
              val => !val || (!val.includes("_") && moment(val, "MM-DD-YYYY").isValid())
            )
          })}
        >
          {props => {
            const {
              dirty,
              values,
              touched,
              errors,
              initialTouched,
              isSubmitting,
              isValid,
              handleSubmit,
              setFieldValue
            } = props;
            return (
              <form onSubmit={handleSubmit}>
                <label
                  htmlFor="givenName"
                  className={values.givenName ? "has-required" : "needs-required"}
                >
                  First Name
                </label>
                <Field
                  id="givenName"
                  name="givenName"
                  type="text"
                  className={
                    errors.givenName && touched.givenName ? "text-input error" : "text-input"
                  }
                />
                {errors.givenName && touched.givenName && (
                  <div className="formik-error">{errors.givenName}</div>
                )}

                <label
                  htmlFor="familyName"
                  className={values.familyName ? "has-required" : "needs-required"}
                >
                  Last Name
                </label>
                <Field
                  id="familyName"
                  name="familyName"
                  type="text"
                  className={
                    errors.familyName && touched.familyName ? "text-input error" : "text-input"
                  }
                />
                {errors.familyName && touched.familyName && (
                  <div className="formik-error">{errors.familyName}</div>
                )}

                <div className="flex-end mt-20">
                  <button
                    type="submit"
                    className={dirty && isValid ? "highlight" : ""}
                    disabled={!initialTouched || !dirty || !isValid || isSubmitting}
                  >
                    {actionText}
                  </button>
                </div>

                <hr></hr>

                <h3>Optional Fields</h3>

                <label htmlFor="phone">Phone Number</label>
                <Field
                  id="phone"
                  name="phone"
                  type="text"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    this.updatePhone(e, setFieldValue)
                  }
                  className={errors.phone && touched.phone ? "text-input error" : "text-input"}
                />
                {errors.phone && touched.phone && (
                  <div className="formik-error">{errors.phone}</div>
                )}

                <div className="flex-between-bottom mb-10 mt-20">
                  <label htmlFor="location">Location</label>
                  <div>
                    <Select
                      className="location-select"
                      placeholder="Location Filter"
                      defaultValue={this.state.selectedLocation}
                      onChange={e => this.updateLocation(e, setFieldValue)}
                      options={this.locationOptions()}
                    />
                  </div>
                </div>
                <Field
                  id="location"
                  name="location"
                  type="text"
                  className={
                    errors.location && touched.location ? "text-input error" : "text-input"
                  }
                />
                {errors.location && touched.location && (
                  <div className="formik-error">{errors.location}</div>
                )}

                <label htmlFor="dateOfBirth" className="mt-20">
                  Date of Birth
                </label>
                <Field name="dateOfBirth" component={FormikDob} />

                <label htmlFor="email">Email</label>
                <Field
                  id="email"
                  name="email"
                  type="text"
                  className={errors.email && touched.email ? "text-input error" : "text-input"}
                />
                {errors.email && touched.email && (
                  <div className="formik-error">{errors.email}</div>
                )}

                <label htmlFor="accountId">Account Id</label>
                <Field
                  id="accountId"
                  name="accountId"
                  type="text"
                  className={
                    errors.accountId && touched.accountId ? "text-input error" : "text-input"
                  }
                />
                {errors.accountId && touched.accountId && (
                  <div className="formik-error">{errors.email}</div>
                )}

                <label htmlFor="mrn">MRN</label>
                <Field
                  id="mrn"
                  name="mrn"
                  type="text"
                  className={errors.mrn && touched.mrn ? "text-input error" : "text-input"}
                />
                {errors.mrn && touched.mrn && <div className="formik-error">{errors.mrn}</div>}
              </form>
            );
          }}
        </Formik>
      </div>
    );
  }
}

export default PatientAddEdit;
